'use client';

import { useMemo } from 'react';
import type { ReadonlyURLSearchParams } from 'next/navigation';

/**
 * Parsing de los parámetros de URL de /reservas (extraído de
 * AvailabilitySearch en F11b del plan maestro — misma semántica).
 * Los filtros de scope (building/neighborhood/source) se preservan al hacer
 * nuevas búsquedas; intent=wsp abre el asistente de WhatsApp.
 */
export function useAvailabilityFilters(searchParams: ReadonlyURLSearchParams) {
  const urlArrival = searchParams.get('arrival') || '';
  const urlDeparture = searchParams.get('departure') || '';
  const urlNumAdults = searchParams.get('numAdults') || '2';
  const urlGuestAdults = searchParams.get('guestAdults') || '';
  const urlChildrenAges = searchParams.get('childrenAges') || '';
  const urlQuery = searchParams.get('q') || ''; // Capturar query del usuario
  // Filtros de scope (preservados al hacer nuevas búsquedas).
  const urlBuilding = searchParams.get('building') || '';
  const urlNeighborhood = searchParams.get('neighborhood') || '';
  const urlSource = searchParams.get('source') || '';
  const urlIntent = searchParams.get('intent') || '';

  // Etiqueta de contexto visible cuando se está buscando filtrado por edificio o barrio.
  // Las únicas landings hoy son 'nuevo-conquistador' / 'El Laguito'; cualquier otro
  // building/neighborhood se muestra con su slug capitalizado como fallback.
  const contextHeading = useMemo(() => {
    if (urlBuilding === 'nuevo-conquistador') {
      return 'Disponibilidad en Edificio Nuevo Conquistador';
    }
    if (urlBuilding) {
      return `Disponibilidad en ${urlBuilding}`;
    }
    if (urlNeighborhood) {
      return `Disponibilidad en ${urlNeighborhood}`;
    }
    return null;
  }, [urlBuilding, urlNeighborhood]);

  const contextScope = useMemo<'building' | 'neighborhood' | 'all'>(() => {
    if (urlBuilding) return 'building';
    if (urlNeighborhood) return 'neighborhood';
    return 'all';
  }, [urlBuilding, urlNeighborhood]);

  return {
    urlArrival,
    urlDeparture,
    urlNumAdults,
    urlGuestAdults,
    urlChildrenAges,
    urlQuery,
    urlBuilding,
    urlNeighborhood,
    urlSource,
    urlIntent,
    contextHeading,
    contextScope,
  };
}
